import { useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { CalendarTodayOutlined as CalendarIcon } from '@mui/icons-material';

import {
  FORTA_WHITE,
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_SUCCESS,
  FORTA_YELLOW,
  FORTA_ERROR,
} from '../../../utils/colors';
import { isDateOlderThan24Hours, supervisionTypeStyles } from './utils';
import { formatEntityId, getEndDateFromStartDateAndDuration, history } from '../../../helpers';
import { TreatmentPlanStatus } from '../treatment-plan/constants/general';
import { ActionRequiredItemEnum } from '../../../constants';
import BcbaIcon from '../../../assets/images/bcba.svg';
import EditIcon from '../../../assets/images/edit-new.svg';
import ProgressIcon from '../../../assets/images/progress-new.svg';
import ActiveEditingIcon from '../../../assets/images/active-editing-new.svg';
import ClearSignatureIcon from '../../svgs/ClearSignatureIcon';
import ActiveIcon from '../../svgs/ActiveIcon';

import type { RootState } from '../../../store';
import type { ITreatmentPlan } from '../../../utils/types';

interface TreatmentPlanItemProps {
  treatmentPlan: ITreatmentPlan;
  sx?: any;
  isActionRequiredItem?: boolean;
}

function TreatmentPlanItem(props: TreatmentPlanItemProps) {
  const { treatmentPlan, sx, isActionRequiredItem = false } = props;
  const { tpStatusIdByLabel } = useSelector((state: RootState) => state.treatmentPlans);
  const isSigned = treatmentPlan?.signature;
  const tpCardStyles = supervisionTypeStyles[ActionRequiredItemEnum.TreatmentPlan];
  const showBorder =
    isActionRequiredItem &&
    treatmentPlan.status?.date &&
    isDateOlderThan24Hours(treatmentPlan.status?.date);
  const handleClick = () => {
    if (!treatmentPlan) return;
    history.navigate(`/treatment-plan/${treatmentPlan.id}/client-info`);
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        color: FORTA_GRAY4,
        minHeight: isActionRequiredItem ? '210px' : '142px',
        maxHeight: isActionRequiredItem ? '210px' : '142px',
        border: showBorder ? `1px solid ${FORTA_ERROR}` : 'none',
        ...sx,
      }}
      onClick={handleClick}
      data-testid={`treatmentPlan-${treatmentPlan.client?.first_name}_${treatmentPlan.is_active}_${treatmentPlan.status?.label}`}
    >
      {isActionRequiredItem && (
        <Box
          sx={{
            ml: 1.2,
            my: 1.5,
            width: '5px',
            backgroundColor: tpCardStyles.color,
            borderRadius: '8px',
          }}
        />
      )}
      <Box
        sx={{
          px: 2,
          py: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 230,
          gap: 1,
        }}
        onClick={handleClick}
      >
        {isActionRequiredItem && (
          <Typography fontSize={16} fontWeight={700} mt={1} align="left">
            {tpCardStyles.name}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            my: 0.5,
            pt: !isActionRequiredItem ? 0.5 : 0,
          }}
        >
          {treatmentPlan?.is_active ? (
            <img src={ActiveEditingIcon} alt="Active Editing" width={24} height={24} />
          ) : treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ? (
            <img src={EditIcon} alt="Edit" width={24} height={24} />
          ) : (
            treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started] && (
              <img src={ProgressIcon} alt="Progress" width={24} height={24} />
            )
          )}
          <Typography
            ml={1}
            color={FORTA_GRAY4}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {treatmentPlan.client?.first_name} {treatmentPlan.client?.last_name}
          </Typography>
        </Box>

        {isActionRequiredItem ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
            <CalendarIcon sx={{ color: FORTA_BLUE.primary, height: 14, width: 14 }} />
            <Typography display="inline" ml={0.5} fontSize={14}>
              {treatmentPlan.treatment_recommendation?.date_start
                ? moment(treatmentPlan.treatment_recommendation.date_start).format('MM/DD/YY')
                : 'N/A'}{' '}
              -&nbsp;
              {treatmentPlan.treatment_recommendation?.duration
                ? moment(
                    getEndDateFromStartDateAndDuration(
                      treatmentPlan.treatment_recommendation.date_start,
                      treatmentPlan.treatment_recommendation.duration,
                      'months',
                    ),
                  ).format('MM/DD/YY')
                : 'N/A'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
            <img width={16} src={BcbaIcon} alt="BT icon" />
            <Typography display="inline" ml={0.5} fontSize={14}>
              {treatmentPlan.provider?.first_name} {treatmentPlan.provider?.last_name}
            </Typography>
          </Box>
        )}

        {isActionRequiredItem && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ActiveIcon color={FORTA_BLUE.primary} />
            <Typography display="inline" ml={0.5} fontSize={14}>
              {treatmentPlan.is_active ? 'Active' : 'Not Active'}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            my: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 24,
              px: 1,
              backgroundColor: FORTA_BLUE.light3,
              borderRadius: '16px',
              color: FORTA_BLUE.primary,
              width: 'auto',
            }}
          >
            <Typography
              fontSize={14}
              fontWeight={700}
              px={0.5}
              sx={{
                backgroundColor: FORTA_BLUE.light3,
                borderRadius: '16px',
                color: FORTA_BLUE.primary,
                textAlign: 'center',
              }}
            >
              {formatEntityId(treatmentPlan.id ?? '', 'FTP')}
            </Typography>
          </Box>
          {!isActionRequiredItem && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                height: 25,
                mt: 0.2,
                pr: isSigned ? 0 : 1,
                ml: 1,
                backgroundColor: isSigned ? 'transparent' : FORTA_BLUE.light3,
                borderRadius: '16px',
              }}
            >
              <Box
                sx={{
                  backgroundColor: isSigned ? FORTA_SUCCESS : FORTA_YELLOW,
                  width: '25px',
                  height: '25px',
                  borderRadius: '50%',
                  py: 0.4,
                  p: 0.55,
                }}
              >
                <ClearSignatureIcon color={isSigned ? FORTA_WHITE : FORTA_BLUE.primary} />
              </Box>
              {isSigned ? null : (
                <Typography ml={1} fontSize={12} color={FORTA_BLUE.primary}>
                  BCBA
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default TreatmentPlanItem;
